/* Native Shopify navigation only. The storefront forbids iframe embedding. */
(() => {
  'use strict';
  if (window.MxNewsletterNative) return;
  window.MxNewsletterNative = true;
  const selector = 'form[data-mx-newsletter]';
  const key = 'mx-newsletter-return-v2';
  const states = new Map();
  let active = null;
  function forget() { try { sessionStorage.removeItem(key); } catch (_) {} }
  function remember(value) { try { sessionStorage.setItem(key, JSON.stringify(value)); } catch (_) {} }
  function readReturn() {
    try {
      const value = JSON.parse(sessionStorage.getItem(key));
      if (value && typeof value.id === 'string' && value.path === location.pathname &&
          Number.isFinite(value.at) && Date.now() - value.at >= 0 && Date.now() - value.at < 600000) return value;
    } catch (_) {}
    return null;
  }
  function cleanUrl(form) {
    const url = new URL(location.href);
    ['customer_posted', 'contact_posted', 'form_type', 'form_key'].forEach(name => url.searchParams.delete(name));
    if (url.searchParams.get('view') === 'mxw-newsletter') url.searchParams.delete('view');
    url.hash = form.id;
    return url;
  }
  function setMessage(state, kind, text) {
    state.status.hidden = false;
    state.status.dataset.state = kind;
    state.status.setAttribute('role', kind === 'error' || kind === 'unknown' ? 'alert' : 'status');
    if (text) state.status.textContent = text;
    state.form.dataset.mxState = kind;
    window.dispatchEvent(new Event('resize'));
  }
  function uncertain(state) {
    state.form.removeAttribute('aria-busy');
    state.button.textContent = 'Not confirmed';
    setMessage(state, 'unknown', 'Your subscription has not been confirmed. Complete any visible Shopify security check. If nothing happens, reload this form before trying again.');
    if (state.actions.childElementCount) return;
    const link = document.createElement('a');
    const url = cleanUrl(state.form);
    link.href = url.href;
    link.textContent = 'Reload form';
    link.addEventListener('click', event => {
      forget();
      if (url.pathname === location.pathname && url.search === location.search) {
        event.preventDefault();
        location.reload(); // A fresh GET, never a repeat POST or hash-only navigation.
      }
    });
    state.actions.append(link);
  }
  function begin(state) {
    active = state;
    state.phase = 'pending';
    state.email.readOnly = true; // Keep contact[email] in the native POST payload.
    state.button.disabled = true;
    state.button.textContent = 'Submitting…';
    state.email.removeAttribute('aria-invalid');
    state.form.setAttribute('aria-busy', 'true');
    state.actions.replaceChildren();
    const url = cleanUrl(state.form);
    // Preserve Shopify's action and validation handlers; only specify the documented return path.
    state.form.target = '_self';
    state.returnInput.value = url.pathname + url.search + url.hash;
    remember({ id: state.form.id, path: location.pathname, at: Date.now(), offset: state.form.getBoundingClientRect().top });
    setMessage(state, 'pending', 'Submitting… If Shopify asks for verification, complete it to return to this form.');
    state.timer = setTimeout(() => { if (active === state) uncertain(state); }, 45000);
  }
  function restorePosition(state, record) {
    // The theme finishes its font-based sizing first. Never keep pulling the user back while scrolling.
    let cancelled = false;
    const cancel = () => { cancelled = true; };
    const events = ['wheel', 'touchstart', 'keydown', 'pointerdown'];
    events.forEach(event => window.addEventListener(event, cancel, { once: true, passive: true }));
    Promise.resolve(document.fonts?.ready).then(() => requestAnimationFrame(() => requestAnimationFrame(() => {
      events.forEach(event => window.removeEventListener(event, cancel));
      if (cancelled || !state.form.isConnected) return;
      const offset = Number.isFinite(record?.offset) ? Math.min(Math.max(record.offset, 80), innerHeight / 2) : 100;
      window.scrollTo({ top: Math.max(0, scrollY + state.form.getBoundingClientRect().top - offset), behavior: 'instant' });
      state.status.focus({ preventScroll: true });
    })));
  }
  function mount(scope = document) {
    scope.querySelectorAll(selector).forEach(form => {
      if (states.has(form)) return;
      const email = form.querySelector('input[name="contact[email]"]');
      const button = form.querySelector('button[type="submit"]');
      const status = form.querySelector('[data-mx-newsletter-status]');
      if (!email || !button || !status) return;
      let returnInput = form.querySelector('input[name="return_to"]');
      if (!returnInput) {
        returnInput = document.createElement('input');
        returnInput.type = 'hidden';
        returnInput.name = 'return_to';
        form.append(returnInput); // Also supports updating only this asset on the published Inline theme.
      }
      const returnUrl = cleanUrl(form);
      returnInput.value = returnUrl.pathname + returnUrl.search + returnUrl.hash;
      form.target = '_self';
      const actions = document.createElement('div');
      actions.className = 'mxn-actions';
      form.append(actions);
      const state = { form, email, button, status, returnInput, actions, phase: 'idle', originalButton: [...button.childNodes].map(node => node.cloneNode(true)) };
      states.set(form, state);
      email.addEventListener('input', () => {
        if (state.phase !== 'success' || !email.value.trim() || email.value.trim().toLowerCase() === state.acceptedEmail) return;
        state.phase = 'idle';
        form.dataset.mxState = 'idle';
        button.disabled = false;
        button.replaceChildren(...state.originalButton.map(node => node.cloneNode(true)));
        status.hidden = true;
      });
    });
  }
  function settleReturn() {
    const record = readReturn();
    let anchor;
    try { anchor = decodeURIComponent(location.hash.slice(1)); } catch (_) {}
    const preferred = [...states.values()].find(state => state.form.id === anchor) ||
      [...states.values()].find(state => state.form.id === record?.id);
    const results = [...states.values()].filter(state => state.status.querySelector('[data-mx-newsletter-result]'));
    const state = preferred || results[0];
    if (!state) return;
    const result = state.status.querySelector('[data-mx-newsletter-result]');
    if (!result) {
      if (record?.id === state.form.id && anchor === state.form.id) {
        active = state;
        state.phase = 'pending';
        state.button.disabled = true;
        uncertain(state); // Returning without a server result is never a success.
      }
      return;
    }
    const kind = result.dataset.mxNewsletterResult;
    if (kind !== 'success' && kind !== 'error') return;
    forget();
    // Shopify can repeat the customer result across forms; show it only at the originating entry.
    results.forEach(other => { if (other !== state) other.status.hidden = true; });
    state.phase = kind;
    setMessage(state, kind);
    if (kind === 'success') {
      state.acceptedEmail = state.email.value.trim().toLowerCase();
      state.button.disabled = true;
      state.button.textContent = 'Subscribed';
    } else state.email.setAttribute('aria-invalid', 'true');
    restorePosition(state, record);
  }
  window.addEventListener('submit', event => {
    const state = states.get(event.target);
    if (!state) return;
    if (active || state.phase === 'success') {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (active && active !== state) setMessage(state, 'pending', 'A subscription is already being processed. Please wait for its result.');
      return;
    }
    if (!state.form.checkValidity()) return;
    begin(state);
    // Do not prevent the first submission. Shopify keeps control of CAPTCHA and native navigation.
  }, true);
  window.addEventListener('pageshow', event => {
    if (event.persisted && active) { clearTimeout(active.timer); uncertain(active); }
  });
  document.addEventListener('shopify:section:load', event => mount(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const [form, state] of states) if (event.target.contains(form)) {
      clearTimeout(state.timer);
      if (active === state) { active = null; forget(); }
      states.delete(form);
    }
  });
  const boot = () => { mount(); settleReturn(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
